import { format } from 'date-fns';
import {
  ContractorCredentialingSummary,
  ContractorOwner,
  ContractorPayerCompliance,
  InsuranceRequirementCompliance,
  SaveContractorOwnerRequest,
} from '@src/types/contractor';
import {
  ApprovalStatus,
  ComplianceLogEntry,
  CredentialRecord,
  NoteAuditEntry,
  PayerApproval,
  PersonnelNote,
  PersonnelNoteInput,
} from '@src/types/personnel';
import { getValidity } from '@component/ContractorPortal/Credentialing/shared/helpers';
import { mockApiError } from '../credentialingContract';

const PAYERS = ['IEHP Health Plan', 'LA Care Health Plan', 'Molina Healthcare'];
const [IEHP, LA_CARE, MOLINA] = PAYERS;

const now = () => format(new Date(), 'MM/dd/yyyy HH:mm');

const ME = { name: 'Ana Rivera', role: 'Contractor Credentialing User' };
const ADMIN = { name: 'James Okafor', role: 'Contractor User Admin' };

/* ---------------------------------------------------- insurance records */

const approvalsOf = (
  ...entries: Array<[string, ApprovalStatus, string?]>
): Array<PayerApproval> =>
  entries.map(([payerName, status, rejectionReason]) => ({
    payerId: PAYERS.indexOf(payerName) + 1,
    payerName,
    status,
    ...(status === 'Rejected' && {
      decidedAt: '10/10/2026',
      rejectionReason,
    }),
  }));

const insurance = (
  id: number,
  type: string,
  policyNumber: string,
  carrier: string,
  certificateHolder: string,
  effectiveDate: string,
  expirationDate: string,
  approvals: Array<PayerApproval>
): CredentialRecord => ({
  approvals,
  files: [],
  id,
  kind: 'permit',
  type,
  validity: getValidity(expirationDate) ?? null,
  values: {
    carrier,
    certificateHolder,
    effectiveDate,
    expirationDate,
    policyNumber,
  },
});

const GL = 'General Liability';
const CAL = 'Commercial Auto Liability';
const WC = 'Workers’ Compensation';
const TRAVELERS = 'Travelers';
const PROGRESSIVE = 'Progressive Commercial';
const SCIF = 'State Compensation Insurance Fund';

let records: Array<CredentialRecord> = [
  insurance(
    1,
    GL,
    'GL-88213',
    TRAVELERS,
    IEHP,
    '2026-01-01',
    '2027-01-01',
    approvalsOf([IEHP, 'Approved'], [LA_CARE, 'Approved'])
  ),
  insurance(
    2,
    GL,
    'GL-88213',
    TRAVELERS,
    MOLINA,
    '2026-01-01',
    '2027-01-01',
    approvalsOf([MOLINA, 'Approved'])
  ),
  insurance(
    3,
    GL,
    'GL-77104',
    TRAVELERS,
    IEHP,
    '2025-01-01',
    '2026-01-01',
    approvalsOf([IEHP, 'Approved'], [LA_CARE, 'Approved'])
  ),
  insurance(
    4,
    CAL,
    'CA-61208',
    PROGRESSIVE,
    IEHP,
    '2026-11-15',
    '2027-11-15',
    approvalsOf(
      [
        IEHP,
        'Rejected',
        'The policy number on the certificate doesn’t match the one entered.',
      ],
      [LA_CARE, 'Pending Review']
    )
  ),
  insurance(
    5,
    CAL,
    'CA-55120',
    PROGRESSIVE,
    IEHP,
    '2025-11-15',
    '2026-11-15',
    approvalsOf([IEHP, 'Approved'], [LA_CARE, 'Approved'])
  ),
  insurance(
    6,
    WC,
    'WC-91822',
    SCIF,
    IEHP,
    '2026-10-01',
    '2027-10-01',
    approvalsOf([IEHP, 'Pending Review'])
  ),
  insurance(
    7,
    WC,
    'WC-91822',
    SCIF,
    MOLINA,
    '2026-10-01',
    '2027-10-01',
    approvalsOf([MOLINA, 'Pending Review'])
  ),
  insurance(
    8,
    WC,
    'WC-80455',
    SCIF,
    IEHP,
    '2025-10-01',
    '2026-10-01',
    approvalsOf([IEHP, 'Approved'])
  ),
  insurance(
    9,
    WC,
    'WC-80455',
    SCIF,
    MOLINA,
    '2025-10-01',
    '2026-10-01',
    approvalsOf([MOLINA, 'Approved'])
  ),
];

const LOCKING_STATUSES: Array<ApprovalStatus> = [
  'In Review',
  'Approved',
  'Approved automatically',
];

const findRecord = (id: number) => {
  const found = records.find((item) => item.id === id);
  if (!found) throw mockApiError(404, 'Record not found');
  return found;
};

type SaveInsuranceMock = {
  id?: number;
  type: string;
  values: Record<string, string | null>;
};

/* ----------------------------------------------------------- owners */

let owners: Array<ContractorOwner> = [
  {
    email: 'robert.chen@abctransportation.example',
    firstName: 'Robert',
    id: 1,
    lastName: 'Chen',
    phone: '(626) 555-0110',
    status: 'Active',
    title: 'Owner and President',
  },
  {
    email: 'lena.park@abctransportation.example',
    firstName: 'Lena',
    id: 2,
    lastName: 'Park',
    phone: '(310) 555-0172',
    status: 'Active',
    title: 'Co-Owner, Operations',
  },
  {
    email: null,
    firstName: 'Daniel',
    id: 3,
    lastName: 'Reyes',
    phone: null,
    status: 'Inactive',
    title: 'Former Partner',
  },
];

// Active owners first, then by last name (WF-C5 card 1)
const sortedOwners = () =>
  [...owners].sort(
    (a, b) =>
      Number(a.status === 'Inactive') - Number(b.status === 'Inactive') ||
      a.lastName.localeCompare(b.lastName)
  );

/* ------------------------------------------------------------ notes */

type MockNote = PersonnelNote & { audit: Array<NoteAuditEntry> };

const noteOf = (
  id: number,
  createdAt: string,
  author: typeof ME,
  category: PersonnelNote['category'],
  text: string
): MockNote => ({
  audit: [
    {
      action: 'Created',
      at: createdAt,
      id,
      newValue: text,
      originalValue: null,
      role: author.role,
      userName: author.name,
    },
  ],
  authorName: author.name,
  authorRole: author.role,
  category,
  createdAt,
  id,
  isOwn: author.name === ME.name,
  text,
});

let notes: Array<MockNote> = [
  noteOf(
    1,
    '10/12/2026 14:05',
    ME,
    'Credentialing Record Modified',
    'Owner title updated to include safety.'
  ),
  noteOf(
    2,
    '10/01/2026 13:12',
    ME,
    'Credentialing Record Note',
    'Workers’ compensation renewal uploaded and sent to payers.'
  ),
  noteOf(
    3,
    '09/04/2026 09:30',
    ADMIN,
    'Credentialing Record Note',
    'Commercial auto renewal due before 11/15; broker has been asked for the new certificate.'
  ),
];

const withoutNoteAudit = ({ audit, ...note }: MockNote): PersonnelNote => note;

/* ------------------------------------------------- payer compliance */

const requirementOf = (
  id: number,
  name: string,
  recordId: number | null,
  details: string,
  approval: ApprovalStatus
): InsuranceRequirementCompliance => {
  const record = recordId ? records.find((item) => item.id === recordId) : null;

  return {
    approval,
    details,
    id,
    kind: 'insurance',
    name,
    // locked while the payer reviews it and once it approved it (ST-C2)
    recordEditable: !LOCKING_STATUSES.includes(approval),
    recordId,
    recordType: name,
    status:
      approval === 'Approved' && record?.validity !== 'Expired'
        ? 'Compliant'
        : 'Non-Compliant',
    validity: record?.validity ?? null,
  };
};

const payerCompliance = (): Array<ContractorPayerCompliance> => [
  {
    certificateHolder: `${IEHP} required`,
    payerId: 1,
    payerName: IEHP,
    requirements: [
      requirementOf(1, GL, 1, 'Expires 01/01/2027', 'Approved'),
      requirementOf(
        2,
        CAL,
        5,
        'Expires 11/15/2026 · renewal rejected',
        'Approved'
      ),
      requirementOf(
        3,
        WC,
        6,
        'Previous policy expired 10/01/2026 · renewal sent 10/01/2026',
        'Pending Review'
      ),
      requirementOf(
        4,
        'Professional Liability',
        null,
        'Added to the template 09/01/2026 · no record yet',
        'Not added'
      ),
    ],
    status: 'Non-Compliant',
    templateName: 'IEHP Insurance Requirements',
    version: 'Version 2 · effective 09/01/2026',
  },
  {
    certificateHolder: null,
    payerId: 2,
    payerName: LA_CARE,
    requirements: [
      requirementOf(5, GL, 1, 'Expires 01/01/2027', 'Approved'),
      requirementOf(
        6,
        CAL,
        5,
        'Expires 11/15/2026 · renewal pending review',
        'Approved'
      ),
    ],
    status: 'Compliant',
    templateName: 'LA Care Insurance Requirements',
    version: 'Version 1 · effective 01/08/2026',
  },
  {
    certificateHolder: `${MOLINA} required`,
    payerId: 3,
    payerName: MOLINA,
    requirements: [
      requirementOf(7, GL, 2, 'Expires 01/01/2027', 'Approved'),
      requirementOf(
        8,
        WC,
        7,
        'Previous policy expired 10/01/2026 · renewal sent 10/01/2026',
        'Pending Review'
      ),
    ],
    status: 'Non-Compliant',
    templateName: 'Molina Insurance Requirements',
    version: 'Version 1 · effective 01/20/2026',
  },
];

const logEntry = (
  id: number,
  payerName: string,
  startDate: string,
  endDate: string | null,
  isProjected = false
): ComplianceLogEntry => ({
  endDate,
  entryType: 'Compliancy',
  id,
  isProjected,
  payerName,
  startDate,
});

export const contractorCredentialingMocks = {
  summary: (): ContractorCredentialingSummary => ({
    compliantPayers: payerCompliance().filter(
      ({ status }) => status === 'Compliant'
    ).length,
    effectiveDate: '01/05/2026',
    expirationDate: null,
    id: 1,
    linkedPayers: PAYERS.length,
    name: 'ABC Transportation',
    nextExpiration: { date: '11/15/2026', insuranceType: CAL },
    status: 'Active',
  }),

  // by insurance type, the newest policy first, as the design groups them
  insurance: (): Array<CredentialRecord> =>
    [...records].sort(
      (a, b) =>
        a.type.localeCompare(b.type) ||
        (b.values.expirationDate ?? '').localeCompare(
          a.values.expirationDate ?? ''
        )
    ),
  saveInsurance: ({
    id,
    type,
    values,
  }: SaveInsuranceMock): CredentialRecord => {
    if (id) {
      const current = findRecord(id);
      if (
        current.approvals.some(({ status }) =>
          LOCKING_STATUSES.includes(status)
        )
      ) {
        throw mockApiError(423, 'This record is locked');
      }

      const updated: CredentialRecord = {
        ...current,
        type,
        validity: getValidity(values.expirationDate) ?? null,
        values: { ...current.values, ...values },
        approvals: current.approvals.map((approval) =>
          approval.status === 'Rejected'
            ? { ...approval, status: 'Pending Review', rejectionReason: null }
            : approval
        ),
      };
      records = records.map((item) => (item.id === id ? updated : item));
      return updated;
    }

    const created = {
      ...insurance(
        Math.max(0, ...records.map((item) => item.id)) + 1,
        type,
        values.policyNumber ?? '',
        values.carrier ?? '',
        values.certificateHolder ?? '',
        values.effectiveDate ?? '',
        values.expirationDate ?? '',
        approvalsOf([values.certificateHolder ?? IEHP, 'Pending Review'])
      ),
    };
    records = [created, ...records];
    return created;
  },

  owners: (): Array<ContractorOwner> => sortedOwners(),
  saveOwner: ({
    id,
    note,
    owner,
  }: SaveContractorOwnerRequest): ContractorOwner => {
    if (id) {
      if (!note) throw mockApiError(400, 'A note is required to save an edit');

      const current = owners.find((item) => item.id === id);
      if (!current) throw mockApiError(404, 'Owner not found');

      const updated: ContractorOwner = { ...current, ...owner };
      owners = owners.map((item) => (item.id === id ? updated : item));
      notes = [
        noteOf(
          Math.max(0, ...notes.map((item) => item.id)) + 1,
          now(),
          ME,
          note.category,
          note.text
        ),
        ...notes,
      ];
      return updated;
    }

    const created: ContractorOwner = {
      ...owner,
      id: Math.max(0, ...owners.map((item) => item.id)) + 1,
    };
    owners = [...owners, created];
    return created;
  },

  payerCompliance,

  complianceLog: (): Array<ComplianceLogEntry> => [
    logEntry(1, MOLINA, '01/20/2026', '10/01/2026', true),
    logEntry(2, LA_CARE, '01/08/2026', '11/15/2026', true),
    logEntry(3, IEHP, '01/05/2026', '09/01/2026'),
  ],

  notes: (): Array<PersonnelNote> => notes.map(withoutNoteAudit),
  createNote: ({ note }: { note: PersonnelNoteInput }): PersonnelNote => {
    const created = noteOf(
      Math.max(0, ...notes.map(({ id }) => id)) + 1,
      now(),
      ME,
      note.category,
      note.text
    );
    notes = [created, ...notes];
    return withoutNoteAudit(created);
  },
  updateNote: ({
    noteId,
    text,
  }: {
    noteId: number;
    text: string;
  }): PersonnelNote => {
    const note = notes.find(({ id }) => id === noteId);
    if (!note) throw mockApiError(404, 'Note not found');

    const edited: MockNote = {
      ...note,
      audit: [
        {
          action: 'Edited',
          at: now(),
          id: note.audit.length + 1,
          newValue: text,
          originalValue: note.text,
          role: ME.role,
          userName: ME.name,
        },
        ...note.audit,
      ],
      text,
    };
    notes = notes.map((item) => (item.id === noteId ? edited : item));
    return withoutNoteAudit(edited);
  },
  noteAudit: ({ noteId }: { noteId: number }): Array<NoteAuditEntry> =>
    notes.find(({ id }) => id === noteId)?.audit ?? [],
};
